/**
 * CORE-AI Slice 2 — app → Training Companion "stop monitoring" bridge.
 *
 * Frozen Definition-of-Done item 4 requires monitoring consent to be explicit
 * AND revocable. Server-side that was already true: consent is durable and
 * shop-scoped, minting requires it, and `record-training-extension-evidence`
 * re-checks it immediately before every insert and returns
 * `TRAINING_MONITORING_CONSENT_REQUIRED` / 403 when it is gone.
 *
 * What was missing was local. A successful opt-out could leave the Companion's
 * provider-page observer running — still reading the URL, title, visibility,
 * scroll position and video time every two seconds — until some later server
 * response happened to reject it. "The database eventually ignores what the
 * observer keeps watching" is not revocation.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT THIS IS, AND WHAT IT IS NOT
 * ═══════════════════════════════════════════════════════════════════════════
 * This is the FAST path, not the authority. It carries NO token, no session id
 * and no secret — it is an instruction to stop, and the extension treats it as
 * one. The durable opt-out row is the authority, and it is written first.
 *
 * It is fire-and-forget by design:
 *
 *   - it is only ever called AFTER the durable revoke returned success;
 *   - it never throws, and its result never gates anything;
 *   - if the Companion is not installed, is asleep, or the tab is gone, the
 *     revocation has still succeeded and the next evidence POST is refused with
 *     403, which runs the same terminal teardown in the extension.
 *
 * That is deliberate: a local bridge failure must never make durable consent
 * revocation fail, or the server would stop being the authority.
 */

export const COMPANION_CONSENT_REVOKED_MESSAGE_TYPE =
  'OPTIBAY_TRAINING_COMPANION_CONSENT_REVOKED';

/**
 * Tell any installed Companion on this device to stop observing now.
 *
 * Returns whether the message was POSTED, which is not the same as whether a
 * Companion received or acted on it — nothing here can or should know that.
 * Callers must not treat `false` as a failed revocation.
 */
export function emitCompanionConsentRevoked(): boolean {
  if (typeof window === 'undefined') return false; // SSR / non-browser
  try {
    // Strict targetOrigin: same-origin listeners only. The content script
    // additionally checks `event.source === window`.
    window.postMessage(
      { type: COMPANION_CONSENT_REVOKED_MESSAGE_TYPE },
      window.location.origin,
    );
    return true;
  } catch {
    // A revocation is never allowed to fail because of the local bridge.
    return false;
  }
}
